'use client';
// SportsMarketCard Component - Sports betting style card for head-to-head matchups
import { useCallback } from 'react';
import Link from 'next/link';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { Box, Button, Card, CardContent, IconButton, Theme, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { ROUTES } from '@/constants';

interface Team {
  id: string;
  name: string;
  abbreviation: string;
  seed?: number;
  price: number;
  color: string;
}

interface SportsMarket {
  id: string;
  sport: string;
  isLive: boolean;
  volume: string;
  teams: [Team, Team];
}

interface SportsMarketCardProps {
  market: SportsMarket;
  onTradeClick?: (marketId: string, teamId: string) => void;
}

export function SportsMarketCard({ market, onTradeClick }: SportsMarketCardProps) {
  const [team1, team2] = market.teams;
  const { t } = useT();

  function createTradeHandler(teamId: string) {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onTradeClick?.(market.id, teamId);
    };
  }

  function handleBookmarkClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  return (
    <Link href={ROUTES.MARKET_DETAILS(market.id)} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: (theme: Theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.4)'
                : '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            '&:last-child': { pb: 2 },
          }}
        >
          {/* Team 1 */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  bgcolor: team1.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#ffffff',
                  }}
                >
                  {team1.abbreviation}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {team1.seed && (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 0.25,
                    }}
                  >
                    {team1.seed}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  {team1.name}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'text.primary',
                minWidth: 50,
                textAlign: 'right',
              }}
            >
              {Math.round(team1.price * 100)}%
            </Typography>
          </Box>

          {/* Team 2 */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  bgcolor: team2.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    color: '#ffffff',
                  }}
                >
                  {team2.abbreviation}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {team2.seed && (
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 0.25,
                    }}
                  >
                    {team2.seed}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: 'text.primary',
                  }}
                >
                  {team2.name}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: 'text.primary',
                minWidth: 50,
                textAlign: 'right',
              }}
            >
              {Math.round(team2.price * 100)}%
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={createTradeHandler(team1.id)}
              sx={{
                flex: 1,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                bgcolor: 'rgba(239, 68, 68, 0.08)',
                color: '#dc2626',
                border: 'none',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  border: 'none',
                },
              }}
            >
              {team1.name}
            </Button>
            <Button
              variant="outlined"
              onClick={createTradeHandler(team2.id)}
              sx={{
                flex: 1,
                py: 1,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                bgcolor: 'rgba(59, 130, 246, 0.08)',
                color: '#2563eb',
                border: 'none',
                '&:hover': {
                  bgcolor: 'rgba(59, 130, 246, 0.15)',
                  border: 'none',
                },
              }}
            >
              {team2.name}
            </Button>
          </Box>

          {/* Footer - Volume and Status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {market.isLive && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#ef4444',
                      position: 'relative',
                      animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          opacity: 1,
                        },
                        '50%': {
                          opacity: 0.5,
                        },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        bgcolor: '#ef4444',
                        animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite',
                      },
                      '@keyframes ping': {
                        '75%, 100%': {
                          transform: 'translate(-50%, -50%) scale(2)',
                          opacity: 0,
                        },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: '#ef4444',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('sports.live').toUpperCase()}
                  </Typography>
                </Box>
              )}
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {market.volume}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                {market.sport}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleBookmarkClick}
                sx={{
                  color: 'text.secondary',
                  p: 0.5,
                  '&:hover': {
                    color: 'text.primary',
                  },
                }}
                aria-label="Save"
              >
                <BookmarkBorderIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
}

export default SportsMarketCard;
